import React, { useState, useEffect, Fragment, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "../services/api";
import Loader from "../components/loader";
import { toast } from "react-hot-toast";
import { FiExternalLink, FiPrinter } from "react-icons/fi";
import { FaRegCheckCircle, FaRegTimesCircle } from "react-icons/fa";

const BudgetDescription = () => {
  const { id } = useParams();
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roadlogItems, setRoadlogItems] = useState([]);
  const [modules, setModules] = useState([]);
  const printRef = useRef(null);

  const getBudget = async () => {
    try {
      const { ok, data } = await api.get(`/budget/public/${id}`);
      if (!ok) throw new Error("Failed to fetch budget");
      setBudget(data);
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  const getModules = async () => {
    try {
      const { ok, data } = await api.post(`/roadlog_module/public/search/`, {
        project_id: budget.projectId,
      });

      if (!ok) {
        throw new Error("Failed to fetch modules");
      }

      return data;
    } catch (e) {
      console.log(e);
      return [];
    }
  };

  const getRoadlogItems = async () => {
    try {
      const { ok, data } = await api.post(`/roadlog_item/public/search/`, {
        project_id: budget.projectId,
        budget_id: id,
        sort: "-estimation",
      });

      if (!ok) {
        throw new Error("Failed to fetch roadlog items");
      }

      return data;
    } catch (e) {
      console.log(e);
      return [];
    }
  };

  const organizeData = async () => {
    const modulesList = await getModules();
    const itemsList = await getRoadlogItems();

    setRoadlogItems(itemsList);

    // Process modules and add items to each module
    const modulesWithItems = modulesList.map((module) => {
      const moduleItems = itemsList.filter((item) => String(item.module_id) === String(module._id));
      return {
        ...module,
        items: moduleItems,
      };
    });

    // Check for items without module
    const itemsWithoutModule = itemsList.filter((item) => !item.module_id);
    if (itemsWithoutModule.length > 0) {
      modulesWithItems.push({
        _id: "general",
        title: "General Features",
        items: itemsWithoutModule,
      });
    }
    setLoading(false);
    console.log(modulesWithItems);
    setModules(modulesWithItems);
  };

  const handlePrint = () => {
    // Set the document title to the budget name for the print
    const originalTitle = document.title;
    document.title = budget?.name || "Budget";

    window.print();

    // Reset the document title after printing
    setTimeout(() => {
      document.title = originalTitle;
    }, 100);
  };

  useEffect(async () => {
    if (id) {
      await getBudget();
    }
  }, [id]);

  useEffect(() => {
    if (budget) {
      organizeData();
    }
  }, [budget]);

  if (loading)
    return (
      <div className="bg-gray-100 min-h-screen p-4 sm:p-8 w-full flex justify-center items-center">
        <div className="w-full max-w-4xl bg-white rounded-lg shadow-md p-8 text-center">
          {" "}
          <Loader />
          <p className="text-gray-600 italic mb-4">Loading budget details...</p>
        </div>
      </div>
    );

  if (!budget)
    return (
      <div className="bg-gray-100 min-h-screen p-4 sm:p-8 w-full flex justify-center items-center">
        <div className="w-full max-w-xl mx-auto bg-white rounded-lg p-8 shadow-md text-center">
          {" "}
          <div className="text-red-600 flex items-center justify-center text-xl mb-4">
            <FaRegTimesCircle className="w-6 h-6 mr-2" />
            Budget not found or access denied.
          </div>
          <p className="text-gray-600">
            Please check the link or contact{" "}
            <a href="mailto:office@selego.co" className="text-sky-700 hover:underline">
              office@selego.co
            </a>{" "}
            if you believe this is an error.
          </p>
        </div>
      </div>
    );

  return (
    <div className="bg-gray-100 min-h-screen p-4 sm:p-8 w-full print:bg-white print:min-h-0 print:p-0 print:h-auto">
      <div className="max-w-7xl bg-white rounded-lg shadow-md mx-auto print:shadow-none print:max-w-none print:h-auto">
        <div className="flex justify-end p-4 print:hidden">
          <button onClick={handlePrint} className="flex items-center gap-2 bg-sky-500 text-white px-4 py-2 rounded hover:bg-sky-600 transition-colors">
            <FiPrinter className="w-4 h-4" />
            Print Budget
          </button>
        </div>
        <div className="border-b border-gray-200 p-6 sm:p-8 print:p-4">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-6">
            <div className="flex items-start gap-4">
              {budget.projectLogo && <img src={budget.projectLogo} alt={budget.projectName} className="w-12 h-12 mt-1 rounded-md bg-gray-100 p-1 object-contain flex-shrink-0" />}
              <div>
                <span className="block text-sm font-medium text-gray-500">{budget.projectName}</span>
                <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 mt-1">{budget.name}</h1>
                {budget.startAt && budget.endAt && (
                  <p className="text-gray-500 mt-1.5 text-sm">
                    Period: {new Date(budget.startAt).toLocaleDateString("fr-FR")} - {new Date(budget.endAt).toLocaleDateString("fr-FR")}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        <div ref={printRef} className="p-6 sm:p-8 space-y-8 print:p-4">
          {budget.description && (
            <section>
              <div className="prose prose-sm max-w-none text-gray-700">
                <div
                  style={{ marginTop: "12px", color: "#374151", fontSize: "14px", lineHeight: "1.5" }}
                  className="comment-content"
                  dangerouslySetInnerHTML={{ __html: budget.description }}
                />
              </div>
            </section>
          )}
          {roadlogItems.length > 0 && (
            <section className="print:break-inside-auto">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 print:break-after-avoid">Features Included</h3>
              <div className="space-y-6">
                {modules.map(
                  (module) =>
                    module.items &&
                    module.items.length > 0 && (
                      <div key={module._id} className="border border-gray-200 rounded-md overflow-hidden print:break-inside-avoid-page">
                        <h4 className="bg-slate-200 px-4 py-2.5 text-base font-medium text-gray-700 border-b border-gray-200 print:bg-gray-100 print:break-after-avoid">
                          {module.title}
                        </h4>
                        <div className="divide-y divide-gray-200">
                          {module.items
                            .sort((a, b) => Number(a.index) - Number(b.index))
                            .map((item) => (
                              <div key={item._id} className="px-4 py-4 print:break-inside-avoid-page">
                                <div className="w-full flex justify-between items-center gap-4 print:flex">
                                  <p className="font-medium text-gray-900">{item.title}</p>
                                  <div className="flex items-center gap-6 print:flex print:items-center print:gap-4 flex-shrink-0">
                                    <div className="flex items-center gap-2 print:flex">
                                      <span className="sm:hidden text-xs text-gray-500">Priority:</span>
                                      <PriorityBadge priority={item.nnl} />
                                    </div>
                                    <div className="flex items-center gap-2 print:flex">
                                      <span className="sm:hidden text-xs text-gray-500">Effort:</span>
                                      <span className="text-gray-600">{item.days_required ? `${item.days_required} days` : "-"}</span>
                                    </div>
                                    <div className="flex items-center gap-2 print:flex">
                                      <span className="sm:hidden text-xs text-gray-500">Cost:</span>
                                      <span className="font-bold text-gray-800">{item.estimation ? `${Number(item.estimation).toLocaleString("fr-FR")} €` : "-"}</span>
                                    </div>
                                  </div>
                                </div>
                                <p
                                  className="text-gray-500 font-thin text-sm prose prose-sm max-w-none mt-2"
                                  dangerouslySetInnerHTML={{ __html: item.comments && item.comments.length > 0 ? item.comments.map((comment) => comment.text).join("") : "" }}
                                />
                              </div>
                            ))}
                        </div>
                      </div>
                    ),
                )}
              </div>
            </section>
          )}
          <div className="border-t border-gray-200 pt-6">
            <div className="text-right flex-shrink-0">
              <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Total Budget</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                {budget.frequency === "monthly" ? `${budget.value.toLocaleString("fr-FR")} € / month` : `${budget.amount.toLocaleString("fr-FR")} €`}
              </h2>
              <p className="text-gray-500 text-xs mt-1 capitalize"> {budget.facturation_type === "regie" ? "Time & Materials" : "Fixed Price"}</p>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-6 text-center text-sm text-gray-600 print:pt-4">
            <p className="mb-4">
              If you have any questions regarding this budget, please contact{" "}
              <a href="mailto:office@selego.co" className="text-sky-700 hover:underline font-medium">
                office@selego.co
              </a>
            </p>
            <a href={`/project/${budget.projectId}/budget-normal`} className="text-sky-700 hover:underline inline-flex items-center gap-1 text-xs print:hidden">
              Go back to project <strong>{budget.projectName}</strong>
              <FiExternalLink className="inline-block w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
      <style jsx global>{`
        /* Force empty paragraphs to display with spacing */
        .comment-content p:empty {
          display: block;
          margin: 1rem 0;
          height: 1rem;
        }

        /* Alternative: target paragraphs with only whitespace */
        .comment-content p:empty,
        .comment-content p:blank {
          display: block !important;
          margin: 1rem 0 !important;
          height: 1rem !important;
          line-height: 1rem !important;
        }

        @media print {
          @page {
            size: A4 portrait;
            margin: 1.5cm;
          }

          html,
          body {
            height: auto !important;
            min-height: auto !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
          }

          /* Container adjustments */
          .max-w-7xl {
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
            overflow: visible !important;
            height: auto !important;
          }

          /* Content flow */
          .space-y-6 > div,
          .space-y-8 > div {
            display: block !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }

          /* Epic containers */
          .divide-y > div {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            margin-bottom: 1rem !important;
          }

          /* Headers */
          h1,
          h2,
          h3,
          h4 {
            page-break-after: avoid !important;
            break-after: avoid !important;
          }

          /* Keep flex for the epic title and metadata row */
          .print\\:flex {
            display: flex !important;
          }

          /* Keep items aligned in the center */
          .print\\:items-center {
            align-items: center !important;
          }

          /* Keep proper spacing between items */
          .print\\:gap-4 {
            gap: 1rem !important;
          }

          /* Force show backgrounds */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          /* Remove any fixed heights */
          .min-h-screen,
          .h-screen {
            height: auto !important;
            min-height: 0 !important;
          }

          /* Ensure content breaks properly */
          section {
            display: block !important;
            page-break-inside: auto !important;
            break-inside: auto !important;
          }

          /* Remove any transforms that might affect printing */
          * {
            transform: none !important;
          }

          /* Ensure proper spacing between elements */
          .space-y-8 > * + * {
            margin-top: 2rem !important;
          }

          /* Remove any overflow restrictions */
          .overflow-hidden,
          .overflow-x-hidden,
          .overflow-y-hidden {
            overflow: visible !important;
          }

          /* Hide print button when printing */
          .print\\:hidden {
            display: none !important;
          }

          /* Ensure each module starts on a new page if needed */
          .print\\:break-inside-avoid-page {
            page-break-inside: avoid !important;
            break-inside: avoid-page !important;
          }

          /* Make sure epic blocks display properly */
          .divide-y > div {
            display: block !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }

          /* Ensure module headers stay with their content */
          h4 + .divide-y {
            page-break-before: avoid !important;
            break-before: avoid !important;
          }

          /* Better spacing for printed content */
          .px-4.py-4 {
            padding: 1rem !important;
          }

          /* Ensure proper display of flex items in print */
          .flex.items-center,
          .flex.justify-between {
            display: flex !important;
          }

          /* Center the first page content */
          body {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
          }

          /* Add top margin to the first page */
          .max-w-7xl {
            margin-top: 2cm !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }

          /* First page padding */
          .max-w-7xl > div:first-child {
            padding-top: 1cm !important;
          }
        }
      `}</style>
    </div>
  );
};

function PriorityBadge({ priority }) {
  if (!priority) return <span className="text-xs text-gray-400">No priority</span>;

  let bgColor = "bg-gray-100";
  let textColor = "text-gray-800";

  if (priority === "NOW") {
    bgColor = "bg-red-100";
    textColor = "text-red-800";
  } else if (priority === "NEXT") {
    bgColor = "bg-orange-100";
    textColor = "text-orange-800";
  }

  return <span className={`inline-block text-xs px-2 py-0.5 rounded font-medium ${bgColor} ${textColor}`}>{priority}</span>;
}

export default BudgetDescription;
